import { useCallback, useRef } from "react";

import { enqueueWorkspaceUserWrite } from "../workspaceUserWriteQueue";
import { useUserPreferences } from "./useUserPreferences";

/**
 * Home v2 choice for this user in this workspace.
 * Shares that scope's WORKSPACE_USER queue with header search and team
 * history. Changes only homeV2 on the snapshot at send time.
 * Success is this write alone.
 */
export const useUserHomeV2 = () => {
  const {
    loading,
    workspaceId,
    userId,
    mergedPreferences,
    workspaceUserPreferences,
    sendWorkspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const loadingRef = useRef(loading);
  const prefsRef = useRef(workspaceUserPreferences);
  const scopeRef = useRef({ workspaceId, userId });
  loadingRef.current = loading;
  prefsRef.current = workspaceUserPreferences;
  scopeRef.current = { workspaceId, userId };

  const setEnabled = useCallback(
    (enabled: boolean): Promise<boolean> =>
      enqueueWorkspaceUserWrite({
        scope: scopeRef.current,
        readScope: () => scopeRef.current,
        field: "homeV2",
        readProps: () => prefsRef.current,
        apply: (latest) => ({
          ...(latest ?? {}),
          homeV2: {
            ...(latest?.homeV2 ?? {}),
            enabled,
          },
        }),
        send: sendWorkspaceUserPreferences ?? updatePreferencesForWorkspaceUser,
        isLoading: () => loadingRef.current,
      }),
    [sendWorkspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  return {
    loading,
    enabled: mergedPreferences?.homeV2?.enabled as boolean | undefined,
    setEnabled,
  };
};
